import { useEffect, useState } from 'react';
import { repairAPI, invoiceAPI, accidentAPI } from '../services/api';
import { formatRs } from '../utils/currency';
import { PageHeader, Button, Modal, Select, Input, Loading, Alert } from '../components/ui';
import RepairDetailsCard from '../components/RepairDetailsCard';
import { useAuth } from '../context/AuthContext';

const fmtDT = (v) => v ? new Date(v).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';
const jobNo = (id) => `JOB-${String(id).padStart(4, '0')}`;

// ── Foreman approval queue ──────────────────────────────────────────────────
// Lists repair requests waiting for review (pending / revision_requested)
// with the FULL submitted details, and approve / reject / request-revision.
export default function Approvals() {
  const { user } = useAuth();
  const [tab,     setTab]     = useState('repairs');
  const [items,   setItems]   = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [accidents, setAccidents] = useState([]);
  const [accSelected, setAccSelected] = useState(null);
  const [mgrItems, setMgrItems] = useState([]);
  const [mgrAccidents, setMgrAccidents] = useState([]);
  const [mgrSelected, setMgrSelected] = useState(null);
  const [mgrForm, setMgrForm] = useState({ action:'approved', remark:'' });
  const [accMgr, setAccMgr] = useState(null);
  const [accMgrForm, setAccMgrForm] = useState({ action:'approved', remark:'' });
  const [accForm, setAccForm] = useState({ action:'approved', remark:'', approved_amount:'' });
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [selected, setSelected] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [form, setForm] = useState({ action:'approved', comments:'' });
  const [invSelected, setInvSelected] = useState(null);
  const [invForm, setInvForm] = useState({ action:'approved', remark:'' });

  const flash = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 3500); };

  const load = () => {
    setLoading(true);
    Promise.all([repairAPI.getAll({ limit: 200 }), invoiceAPI.getAll(),
                 accidentAPI.getAll().catch(() => ({ data: { data: [] } }))])
      .then(([r, i, a]) => {
        setItems((r.data.data || []).filter(x => ['pending','revision_requested'].includes(x.status)));
        setMgrItems((r.data.data || []).filter(x => x.manager_status === 'pending'));
        setMgrAccidents((a.data.data || []).filter(x => x.manager_status === 'pending'));
        setInvoices((i.data.data || []).filter(x => x.status === 'pending_approval'));
        setAccidents((a.data.data || []).filter(x => x.status === 'pending_approval'));
        setMgrAccidents((a.data.data || []).filter(x => x.status === 'pending_manager'));
      })
      .catch(() => setError('Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => { if (user?.role === 'manager') setTab('manager'); }, [user?.role]);

  const open = (r) => { setSelected(r); setForm({ action:'approved', comments:'' }); };

  const submitAccMgr = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await accidentAPI.managerReview(accMgr.id, accMgrForm);
      setAccMgr(null); load();
      flash(accMgrForm.action === 'approved' ? 'Approved — claim record completed' : 'Rejected — returned to officer');
    } catch (err) { setError(err.response?.data?.message || 'Failed to save review'); }
    finally { setSaving(false); }
  };

  const submitManager = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await repairAPI.managerReview(mgrSelected.id, mgrForm);
      setMgrSelected(null); load();
      flash(mgrForm.action === 'approved' ? 'Approved — award letter now available' : 'Rejected — returned to officer');
    } catch (err) { setError(err.response?.data?.message || 'Failed to save review'); }
    finally { setSaving(false); }
  };

  const submitAccident = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (accSelected.__manager) await accidentAPI.managerReview(accSelected.id, accForm);
      else                        await accidentAPI.review(accSelected.id, accForm);
      setAccSelected(null); load();
      flash(accForm.action === 'approved'
        ? (accSelected.__manager ? 'Signed off — record completed' : 'Approved — sent to manager')
        : 'Rejected — returned to officer');
    } catch (err) { setError(err.response?.data?.message || 'Failed to save review'); }
    finally { setSaving(false); }
  };

  const submitInvoice = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await invoiceAPI.review(invSelected.id, invForm);
      setInvSelected(null); load();
      flash(invForm.action === 'approved' ? 'Invoice approved — forwarded to Accounts' : 'Invoice rejected — returned to officer');
    } catch (err) { setError(err.response?.data?.message || 'Failed to save review'); }
    finally { setSaving(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await repairAPI.approve(selected.id, form);
      setSelected(null);
      load();
      flash(`Request ${form.action === 'approved' ? 'approved' : form.action === 'rejected' ? 'rejected' : 'sent back for revision'}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save review');
    } finally { setSaving(false); }
  };

  const urgencyColor = { critical:'bg-red-100 text-red-700', high:'bg-orange-100 text-orange-700', medium:'bg-yellow-100 text-yellow-700', low:'bg-gray-100 text-gray-600' };

  return (
    <div className="space-y-4">
      <PageHeader title="Approvals" subtitle="Foreman review — repair jobs and garage invoices" />

      <div className="flex gap-2">
        <button onClick={() => setTab('repairs')}
          className={`px-4 py-2 rounded-lg text-sm font-medium border ${tab === 'repairs' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white'}`}>
          🔧 Repair Requests ({items.length})
        </button>
        <button onClick={() => setTab('invoices')}
          className={`px-4 py-2 rounded-lg text-sm font-medium border ${tab === 'invoices' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white'}`}>
          🧾 Invoices ({invoices.length})
        </button>
        {['manager','admin'].includes(user?.role) && (
          <button onClick={() => setTab('manager')}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${tab === 'manager' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white'}`}>
            👔 Manager Sign-off ({mgrItems.length + mgrAccidents.length})
          </button>
        )}
        <button onClick={() => setTab('accidents')}
          className={`px-4 py-2 rounded-lg text-sm font-medium border ${tab === 'accidents' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white'}`}>
          💥 Accident Claims ({accidents.length})
        </button>
      </div>
      {error   && <Alert type="error"   onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? <Loading /> : tab === 'repairs' ? ( items.length === 0 ? (
        <div className="bg-white rounded-xl border p-10 text-center text-gray-400">
          🎉 No repair requests waiting for approval
        </div>
      ) : (
        <div className="grid gap-4">
          {items.map(r => (
            <div key={r.id} className="bg-white rounded-xl border p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold text-primary-700">{r.vehicle?.reg_number}</span>
                    <span className="font-medium">{r.title}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${urgencyColor[r.urgency] || ''}`}>{r.urgency}</span>
                    {r.status === 'revision_requested' && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">re-submitted</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Requested by {r.requester?.name || '—'} · {fmtDT(r.created_at)}
                    {r.estimated_cost ? ` · est. ${formatRs(r.estimated_cost)}` : ''}
                  </p>
                </div>
                <Button variant="success" size="sm" onClick={() => open(r)}>Review</Button>
              </div>
            </div>
          ))}
        </div>
      )) : tab === 'manager' ? ( (mgrItems.length + mgrAccidents.length) === 0 ? (
        <div className="bg-white rounded-xl border p-10 text-center text-gray-400">
          🎉 Nothing waiting for manager sign-off
        </div>
      ) : (
        <div className="grid gap-4">
          {mgrAccidents.map(a => (
            <div key={`acc-${a.id}`} className="bg-white rounded-xl border p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold text-primary-700">ACC-{String(a.id).padStart(4,'0')} · {a.vehicle?.reg_number}</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">foreman approved</span>
                    {a.quoted_amount ? <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800">full {formatRs(a.quoted_amount)}</span> : null}
                    {a.approved_amount ? <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">insurance {formatRs(a.approved_amount)}</span> : null}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {a.accident_date} · {a.place || '—'} · driver {a.driver_name || '—'}
                  </p>
                  {(a.balance_payments || []).length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Balance split: {a.balance_payments.map(p =>
                        `${p.payer_type}${p.payer_name ? ' (' + p.payer_name + ')' : ''} ${formatRs(p.amount)}`).join(' · ')}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">Reported by {a.reporter?.name || '—'} · {fmtDT(a.created_at)}</p>
                </div>
                <Button variant="success" size="sm"
                  onClick={() => { setAccSelected({ ...a, __manager: true }); setAccForm({ action:'approved', remark:'', approved_amount: a.approved_amount || '' }); }}>
                  Sign off
                </Button>
              </div>
            </div>
          ))}
          {mgrItems.map(r => (
            <div key={r.id} className="bg-white rounded-xl border p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold text-primary-700">{jobNo(r.id)} · {r.vehicle?.reg_number}</span>
                    <span className="font-medium">{r.title}</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">foreman approved</span>
                    {r.estimated_cost ? <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800">{formatRs(r.estimated_cost)}</span> : null}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {r.repair_type} · requested by {r.requester?.name || '—'} · {fmtDT(r.created_at)}
                  </p>
                </div>
                <Button variant="success" size="sm"
                  onClick={() => { setMgrSelected(r); setMgrForm({ action:'approved', remark:'' }); }}>Sign off</Button>
              </div>
            </div>
          ))}
          {mgrAccidents.map(a => (
            <div key={`acc-${a.id}`} className="bg-white rounded-xl border p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold text-primary-700">ACC-{String(a.id).padStart(4,'0')} · {a.vehicle?.reg_number}</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">foreman approved</span>
                    {a.approved_amount ? <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800">approved {formatRs(a.approved_amount)}</span> : null}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Accident claim · {a.accident_date} · {a.place || '—'} · driver {a.driver_name || '—'}
                  </p>
                </div>
                <Button variant="success" size="sm"
                  onClick={() => { setAccMgr(a); setAccMgrForm({ action:'approved', remark:'' }); }}>Sign off</Button>
              </div>
            </div>
          ))}
        </div>
      )) : tab === 'invoices' ? ( invoices.length === 0 ? (
        <div className="bg-white rounded-xl border p-10 text-center text-gray-400">
          🎉 No invoices waiting for approval
        </div>
      ) : (
        <div className="grid gap-4">
          {invoices.map(inv => (
            <div key={inv.id} className="bg-white rounded-xl border p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold text-primary-700">{jobNo(inv.repair_id)} · {inv.repair?.vehicle?.reg_number || '—'}</span>
                    <span className="font-medium">Invoice {inv.invoice_no}</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800">{formatRs(inv.amount)}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {inv.repair?.title} · submitted by {inv.submitter?.name || '—'} · {fmtDT(inv.created_at)}
                  </p>
                  {inv.description && <p className="text-xs text-gray-500 mt-1">{inv.description}</p>}
                </div>
                <Button variant="success" size="sm"
                  onClick={() => { setInvSelected(inv); setInvForm({ action:'approved', remark:'' }); }}>Review</Button>
              </div>
            </div>
          ))}
        </div>
      )) : ( accidents.length === 0 ? (
        <div className="bg-white rounded-xl border p-10 text-center text-gray-400">
          🎉 No accident claims waiting for approval
        </div>
      ) : (
        <div className="grid gap-4">
          {accidents.map(a => (
            <div key={a.id} className="bg-white rounded-xl border p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold text-primary-700">ACC-{String(a.id).padStart(4,'0')} · {a.vehicle?.reg_number || '—'}</span>
                    {a.third_party ? <span className="px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700">3rd party</span> : null}
                    {a.quoted_amount ? <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800">Quoted {formatRs(a.quoted_amount)}</span> : null}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {a.accident_date}{a.accident_time ? ` ${a.accident_time}` : ''} · {a.place || '—'} · driver {a.driver_name || '—'}
                  </p>
                  {a.damage_details && <p className="text-xs text-gray-500 mt-1">Damages: {a.damage_details}</p>}
                  <p className="text-xs text-gray-400 mt-1">Reported by {a.reporter?.name || '—'} · {fmtDT(a.created_at)}</p>
                </div>
                <Button variant="success" size="sm"
                  onClick={() => { setAccSelected(a); setAccForm({ action:'approved', remark:'', approved_amount: a.approved_amount || a.quoted_amount || '' }); }}>Review</Button>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* ══ ACCIDENT MANAGER SIGN-OFF ═════════════════════════════════════════ */}
      <Modal open={!!accMgr} onClose={() => setAccMgr(null)} size="lg"
        title={`Manager Sign-off — ACC-${accMgr ? String(accMgr.id).padStart(4,'0') : ''}`}>
        {accMgr && (
          <form onSubmit={submitAccMgr} className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-1">
              <p><strong>Vehicle:</strong> {accMgr.vehicle?.reg_number} — {accMgr.vehicle?.brand} {accMgr.vehicle?.model}</p>
              <p><strong>Date / Place:</strong> {accMgr.accident_date} {accMgr.accident_time} · {accMgr.place || '—'}</p>
              <p><strong>Driver:</strong> {accMgr.driver_name || '—'}</p>
              {accMgr.damage_details && <p><strong>Damages:</strong> {accMgr.damage_details}</p>}
              <p><strong>Full amount:</strong> {formatRs(accMgr.quoted_amount)} · <strong>Insurance approved:</strong> {formatRs(accMgr.approved_amount)}</p>
              {(accMgr.balance_payments || []).length > 0 && (
                <div className="pt-1">
                  <strong>Balance split:</strong>
                  <ul className="list-disc ml-5 text-xs text-gray-600">
                    {accMgr.balance_payments.map(p => (
                      <li key={p.id}>{p.payer_type}{p.payer_name ? ` — ${p.payer_name}` : ''}: {formatRs(p.amount)}
                        {p.share_pct ? ` (${p.share_pct}%)` : ''}{p.method ? ` · ${p.method}` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <Select label="Action *" value={accMgrForm.action}
              onChange={e => setAccMgrForm({ ...accMgrForm, action: e.target.value })}>
              <option value="approved">✅ Approve — complete this claim record</option>
              <option value="rejected">❌ Reject — return to officer for correction</option>
            </Select>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remark {accMgrForm.action === 'rejected' && <span className="text-red-500">*</span>}
              </label>
              <textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows="3"
                required={accMgrForm.action === 'rejected'}
                value={accMgrForm.remark}
                onChange={e => setAccMgrForm({ ...accMgrForm, remark: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setAccMgr(null)}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Submit Decision'}</Button>
            </div>
          </form>
        )}
      </Modal>

      {/* ══ MANAGER SIGN-OFF ══════════════════════════════════════════════════ */}
      <Modal open={!!mgrSelected} onClose={() => setMgrSelected(null)} size="xl"
        title={`Manager Sign-off: ${mgrSelected?.title || ''}`}>
        {mgrSelected && (
          <form onSubmit={submitManager} className="space-y-4">
            <RepairDetailsCard repair={mgrSelected} />
            <Select label="Action *" value={mgrForm.action}
              onChange={e => setMgrForm({ ...mgrForm, action: e.target.value })}>
              <option value="approved">✅ Approve — release award letter</option>
              <option value="rejected">❌ Reject — return to officer</option>
            </Select>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remark {mgrForm.action === 'rejected' && <span className="text-red-500">*</span>}
              </label>
              <textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows="3"
                required={mgrForm.action === 'rejected'}
                placeholder={mgrForm.action === 'rejected' ? 'Reason for rejection' : 'Optional remark'}
                value={mgrForm.remark}
                onChange={e => setMgrForm({ ...mgrForm, remark: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setMgrSelected(null)}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Submit Decision'}</Button>
            </div>
          </form>
        )}
      </Modal>

      {/* ══ ACCIDENT CLAIM REVIEW ═════════════════════════════════════════════ */}
      <Modal open={!!accSelected} onClose={() => setAccSelected(null)} size="lg"
        title={`Review Accident Claim: ACC-${accSelected ? String(accSelected.id).padStart(4,'0') : ''}`}>
        {accSelected && (
          <form onSubmit={submitAccident} className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-1">
              <p><strong>Vehicle:</strong> {accSelected.vehicle?.reg_number} — {accSelected.vehicle?.brand} {accSelected.vehicle?.model}</p>
              <p><strong>Date / Place:</strong> {accSelected.accident_date} {accSelected.accident_time} · {accSelected.place || '—'}</p>
              <p><strong>Driver:</strong> {accSelected.driver_name || '—'} {accSelected.driver_licence_no ? `(${accSelected.driver_licence_no})` : ''}</p>
              {accSelected.damage_details && <p><strong>Damages:</strong> {accSelected.damage_details}</p>}
              {accSelected.description && <p><strong>Description:</strong> {accSelected.description}</p>}
              {accSelected.third_party ? <p><strong>Third party:</strong> {accSelected.third_party_desc} · {accSelected.third_party_contact}</p> : null}
              <p><strong>Insurance:</strong> {accSelected.insurance_provider || '—'} · Policy {accSelected.policy_no || '—'}</p>
              <p><strong>Quoted:</strong> {formatRs(accSelected.quoted_amount)}</p>
            </div>
            <Select label="Action *" value={accForm.action}
              onChange={e => setAccForm({ ...accForm, action: e.target.value })}>
              <option value="approved">{accSelected.__manager ? '✅ Approve — complete record' : '✅ Approve — send to manager'}</option>
              <option value="rejected">❌ Reject — return to officer</option>
            </Select>
            {accForm.action === 'approved' && (
              <Input label="Approved Amount by Insurance (Rs.)" type="number" step="0.01"
                value={accForm.approved_amount}
                onChange={e => setAccForm({ ...accForm, approved_amount: e.target.value })} />
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remark {accForm.action === 'rejected' && <span className="text-red-500">*</span>}
              </label>
              <textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows="3"
                required={accForm.action === 'rejected'}
                placeholder={accForm.action === 'rejected' ? 'Reason for rejection (the officer will see this)' : 'Optional remark'}
                value={accForm.remark}
                onChange={e => setAccForm({ ...accForm, remark: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setAccSelected(null)}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Submit Decision'}</Button>
            </div>
          </form>
        )}
      </Modal>

      {/* ══ INVOICE REVIEW ════════════════════════════════════════════════════ */}
      <Modal open={!!invSelected} onClose={() => setInvSelected(null)}
        title={`Review Invoice: ${invSelected?.invoice_no || ''}`}>
        {invSelected && (
          <form onSubmit={submitInvoice} className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-1">
              <p><strong>Job:</strong> {jobNo(invSelected.repair_id)} — {invSelected.repair?.vehicle?.reg_number} · {invSelected.repair?.title}</p>
              <p><strong>Amount:</strong> {formatRs(invSelected.amount)}</p>
              {invSelected.description && <p><strong>Description:</strong> {invSelected.description}</p>}
              <p><strong>Submitted:</strong> {invSelected.submitter?.name} · {fmtDT(invSelected.created_at)}</p>
            </div>
            <Select label="Action *" value={invForm.action}
              onChange={e => setInvForm({ ...invForm, action: e.target.value })}>
              <option value="approved">✅ Approve — forward to Accounts</option>
              <option value="rejected">❌ Reject — return to officer</option>
            </Select>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remark {invForm.action === 'rejected' && <span className="text-red-500">*</span>}
              </label>
              <textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows="3"
                required={invForm.action === 'rejected'}
                placeholder={invForm.action === 'rejected' ? 'Reason for rejection (the officer will see this)' : 'Optional remark'}
                value={invForm.remark}
                onChange={e => setInvForm({ ...invForm, remark: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setInvSelected(null)}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Submit Decision'}</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={!!selected} onClose={() => setSelected(null)}
        title={`Review: ${selected?.title || ''}`} size="xl">
        {selected && (
          <form onSubmit={submit} className="space-y-4">
            <RepairDetailsCard repair={selected} />

            <Select label="Action *" value={form.action}
              onChange={e => setForm({ ...form, action: e.target.value })}>
              <option value="approved">✅ Approve</option>
              <option value="rejected">❌ Reject</option>
              <option value="revision_requested">✏️ Request Revision</option>
            </Select>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comments {form.action !== 'approved' && <span className="text-red-500">*</span>}
              </label>
              <textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows="3"
                required={form.action !== 'approved'}
                placeholder={form.action === 'rejected' ? 'Reason for rejection (the officer will see this)' : 'Optional comments'}
                value={form.comments}
                onChange={e => setForm({ ...form, comments: e.target.value })} />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setSelected(null)}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Submit Decision'}</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
